import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
} from '@mui/material';
import {
  Warning as AlertCircleIcon,
  MenuBook as BookOpenIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Description as FileTextIcon,
  Assignment as FileCheckIcon,
  Gavel as ScaleIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  EmojiEvents as AwardIcon,
  TrendingUp as TrendingUpIcon,
  People as UsersIcon,
  ArrowForward as ArrowRightIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const TakeAction = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const reportExamples = [
    { dept: 'RTO', example: 'Demand for bribe for driving license or vehicle registration' },
    { dept: 'Revenue', example: 'Illegal payment demanded for patta transfer or land documents' },
    { dept: 'Police', example: 'Bribe for station bail or investigation proceedings' },
    { dept: 'Local Bodies', example: 'Unauthorized fees for building permits or trade licenses' },
    { dept: 'TANGEDCO', example: 'Extra payment for new connections or meter changes' },
  ];

  const processSteps = [
    { step: 1, title: 'Submit Report', desc: 'File your complaint through any channel', icon: FileTextIcon },
    { step: 2, title: 'Review', desc: 'Our team verifies and documents the case', icon: FileCheckIcon },
    { step: 3, title: 'Investigation', desc: 'Legal experts investigate the matter', icon: ScaleIcon },
    { step: 4, title: 'Action', desc: 'Legal proceedings or resolution', icon: AlertCircleIcon },
    { step: 5, title: 'Feedback', desc: 'You receive updates on the outcome', icon: CheckCircleIcon },
  ];

  const laws = [
    {
      title: 'Prevention of Corruption Act, 1988',
      description: 'Primary legislation to combat corruption in public offices, defines offenses and penalties.',
      icon: ScaleIcon,
      color: 'error',
    },
    {
      title: 'Right to Information Act, 2005',
      description: 'Empowers citizens to seek information on delayed services or suspected corruption. Use RTI to demand transparency.',
      icon: BookOpenIcon,
      color: 'info',
    },
    {
      title: 'Right to Public Services',
      description: 'Tamil Nadu laws guarantee timely delivery of public services with specified fees and timelines.',
      icon: ShieldIcon,
      color: 'success',
    },
  ];

  const commonServices = [
    { service: 'Patta Transfer', fee: '₹XXX', timeline: '30 days', dept: 'Revenue' },
    { service: 'Birth Certificate', fee: 'Free', timeline: '7 days', dept: 'Local Body' },
    { service: 'Driving License', fee: '₹XXX', timeline: '14 days', dept: 'RTO' },
    { service: 'Electricity Connection', fee: '₹XXX', timeline: '21 days', dept: 'TANGEDCO' },
  ];

  const successStories = [
    {
      title: 'RTO Bribery Case Resolved',
      description: 'A citizen reported a demand for ₹5,000 for a driving license. Our legal team intervened, the license was issued without payment, and the officer was suspended.',
      impact: 'Saved ₹5,000 | Officer Suspended',
      icon: AwardIcon,
    },
    {
      title: 'Revenue Official Held Accountable',
      description: 'Multiple complaints about patta transfer delays led to an investigation. The official was transferred and the pending applications were cleared within a week.',
      impact: '15+ Pattas Issued | Official Transferred',
      icon: TrendingUpIcon,
    },
  ];

  const campaigns = [
    {
      title: 'Zero-Bribe Pledge Campaign',
      description: 'A public awareness campaign encouraging citizens to take a pledge against paying or accepting bribes.',
      status: 'Ongoing',
      participants: '5,000+',
    },
    {
      title: 'RTI Awareness Drives',
      description: 'Training sessions across Tamil Nadu teaching citizens how to file RTI applications effectively.',
      status: 'Monthly',
      participants: '2,500+',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #7f1d1d, #991b1b, #c2410c)',
          py: { xs: 10, lg: 16 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url("/grid.svg")',
            opacity: 0.1,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 20 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h1"
                sx={{
                  color: 'white',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                }}
              >
                Take Action Against Corruption
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#fed7aa',
                  maxWidth: 900,
                  mx: 'auto',
                  mb: 4,
                }}
              >
                Empowering citizens with the tools, knowledge, and support to fight bribery and build a
                transparent Tamil Nadu
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 3,
                  color: 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AlertCircleIcon />
                  <Typography variant="body1">Report Corruption Safely</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BookOpenIcon />
                  <Typography variant="body1">Know Your Rights</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UsersIcon />
                  <Typography variant="body1">Join the Movement</Typography>
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Tab Navigation */}
      <Box sx={{ py: 4, backgroundColor: '#f9fafb', borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 64, zIndex: 30 }}>
        <Container maxWidth="lg">
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
              },
            }}
          >
            <Tab icon={<AlertCircleIcon />} iconPosition="start" label="Report Corruption" />
            <Tab icon={<BookOpenIcon />} iconPosition="start" label="Know Your Rights" />
            <Tab icon={<DownloadIcon />} iconPosition="start" label="Media & Resources" />
          </Tabs>
        </Container>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" sx={{ mb: 3 }}>
                  Report Corruption: Empowering the Citizen
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                  Your voice matters. Report bribery securely and confidentially through multiple
                  channels.
                </Typography>
              </Box>
            </motion.div>

            {/* What to Report */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
                What to Report: Common Examples
              </Typography>
              <Card sx={{ mb: 8 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {reportExamples.map((example, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          gap: 2,
                          p: 2,
                          backgroundColor: '#f9fafb',
                          borderRadius: 2,
                        }}
                      >
                        <Chip
                          label={example.dept}
                          sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
                        />
                        <Typography variant="body1" sx={{ flex: 1 }}>
                          {example.example}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>

            {/* What to Include */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
                What to Include in Your Report
              </Typography>
              <Card sx={{ mb: 8, borderLeft: 4, borderColor: 'secondary.main' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { label: 'Who:', text: 'Name and designation of the official (if known)' },
                      { label: 'When:', text: 'Date and time of the incident' },
                      { label: 'Where:', text: 'Specific office/department location' },
                      { label: 'How much:', text: 'Amount of bribe demanded' },
                      { label: 'Evidence:', text: 'Audio recordings, videos, documents, receipts, or witness details' },
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', mt: 0.5 }} />
                        <Typography variant="body1">
                          <strong>{item.label}</strong> {item.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>

            {/* Process Flow */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
                What Happens After You Report?
              </Typography>
              
              {/* Desktop Timeline */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'space-between', mb: 8, position: 'relative' }}>
                {processSteps.map((step, index) => (
                  <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        mb: 2,
                      }}
                    >
                      {step.step}
                    </Box>
                    <step.icon sx={{ fontSize: 32, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, textAlign: 'center' }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {step.desc}
                    </Typography>
                    {index < processSteps.length - 1 && (
                      <ArrowRightIcon
                        sx={{
                          position: 'absolute',
                          top: 32,
                          right: -16,
                          color: 'primary.main',
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>

              {/* Mobile Timeline */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 3, mb: 8 }}>
                {processSteps.map((step, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                        }}
                      >
                        {step.step}
                      </Box>
                      {index < processSteps.length - 1 && (
                        <Box sx={{ width: 2, height: 64, backgroundColor: '#fecaca', my: 1 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, pb: 3 }}>
                      <step.icon sx={{ fontSize: 24, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </motion.div>

            {/* Whistleblower Protection */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Card sx={{ border: 2, borderColor: 'success.main', backgroundColor: '#dcfce7' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <ShieldIcon sx={{ fontSize: 48, color: 'success.main', flexShrink: 0 }} />
                    <Box>
                      <Typography variant="h4" sx={{ mb: 2 }}>
                        Whistleblower Protection
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>Your identity is protected by law.</strong> Under the Whistleblowers
                        Protection Act and our strict confidentiality protocols, we ensure complete
                        anonymity for all complainants.
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {[
                          'Legal protection under Indian law',
                          'Anonymous reporting options available',
                          'Secure, encrypted data storage',
                          'Our commitment: Your safety is our priority',
                        ].map((text, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockIcon sx={{ fontSize: 20, color: 'success.main' }} />
                            <Typography variant="body2">{text}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Container>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" sx={{ mb: 3 }}>
                  Know Your Rights & Laws: Education is Power
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                  Understanding the law empowers you to fight corruption effectively and fearlessly.
                </Typography>
              </Box>
            </motion.div>

            {/* Key Laws */}
            <Grid container spacing={4} sx={{ mb: 8 }}>
              {laws.map((law, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <Card sx={{ height: '100%', transition: 'box-shadow 0.3s', '&:hover': { boxShadow: 6 } }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: law.color === 'error' ? '#fee2e2' : law.color === 'info' ? '#dbeafe' : '#dcfce7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                          }}
                        >
                          <law.icon
                            sx={{
                              fontSize: 32,
                              color: law.color === 'error' ? 'error.main' : law.color === 'info' ? 'info.main' : 'success.main',
                            }}
                          />
                        </Box>
                        <Typography variant="h5" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>
                          {law.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          {law.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Common Services Table */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
                Citizen Rights & Public Services
              </Typography>
              <Card sx={{ mb: 8 }}>
                <CardContent sx={{ p: 4 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Legal Fee</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Timeline</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commonServices.map((service, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                            <TableCell>{service.service}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{service.fee}</TableCell>
                            <TableCell>{service.timeline}</TableCell>
                            <TableCell>
                              <Chip
                                label={service.dept}
                                size="small"
                                sx={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </motion.div>

            {/* RTI Guide */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Card sx={{ borderLeft: 4, borderColor: 'info.main' }}>
                <CardHeader
                  title={
                    <Typography variant="h4">
                      How to File an RTI Application
                    </Typography>
                  }
                />
                <CardContent>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    The Right to Information Act, 2005 empowers you to seek information about delayed
                    services or suspected corruption. Follow these steps to file an effective RTI:
                  </Typography>
                  <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 1 } }}>
                    <li>Identify the Public Information Officer (PIO) of the relevant department</li>
                    <li>Draft your application clearly stating the information you seek</li>
                    <li>Pay the nominal application fee (₹10) via demand draft or cash</li>
                    <li>Submit the application in person or by registered post</li>
                    <li>Receive response within 30 days (or 48 hours for life-threatening matters)</li>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    <strong>Reference:</strong> Learn more about your rights under the{' '}
                    <Link
                      href="https://www.nathdwaratemple.org/Upload/ContentImages/RTI%20Act%202005.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Right to Information Act, 2005
                    </Link>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        color="info"
                        fullWidth
                        startIcon={<DownloadIcon />}
                        component="a"
                        href="https://cic.gov.in/sites/default/files/RTI-Act_English.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download RTI Act 2005 (PDF)
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<FileTextIcon />}
                        component="a"
                        href="https://rtionline.gov.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        File RTI Online
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Container>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" sx={{ mb: 3 }}>
                  Media & Resources
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                  Real stories of impact, active campaigns, and downloadable resources to empower your
                  fight against corruption.
                </Typography>
              </Box>
            </motion.div>

            {/* Success Stories */}
            <Box sx={{ mb: 8 }}>
              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
                Success Stories
              </Typography>
              <Grid container spacing={4}>
                {successStories.map((story, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    >
                      <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'success.main' }}>
                        <CardContent sx={{ p: 4 }}>
                          <story.icon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                            {story.title}
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {story.description}
                          </Typography>
                          <Chip
                            label={story.impact}
                            sx={{ backgroundColor: '#dcfce7', color: '#166534' }}
                          />
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Active Campaigns */}
            <Box sx={{ mb: 8 }}>
              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
                Active Campaigns
              </Typography>
              <Grid container spacing={4}>
                {campaigns.map((campaign, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    >
                      <Card sx={{ height: '100%' }}>
                        <CardHeader
                          title={campaign.title}
                          action={
                            <Chip
                              label={campaign.status}
                              sx={{ backgroundColor: '#fed7aa', color: '#9a3412' }}
                            />
                          }
                        />
                        <CardContent>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {campaign.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <UsersIcon />
                            <Typography variant="body2" fontWeight={600}>
                              {campaign.participants} participants
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Downloads */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
                Downloadable Resources
              </Typography>
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3}>
                    {[
                      { title: 'RTI Guide (Tamil)', subtitle: 'PDF • தமிழ்', url: 'https://www.tnurbantree.tn.gov.in/nagercoil/wp-content/uploads/sites/36/2020/08/RTI.pdf' },
                      { title: 'RTI Act 2005 (English)', subtitle: 'PDF • Official', url: 'https://cic.gov.in/sites/default/files/RTI-Act_English.pdf' },
                      { title: 'Know Your Rights Flyer', subtitle: 'PDF • Guide', url: 'https://www.crpc.in/doc/Know%20Your%20Rights.pdf' },
                    ].map((resource, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Paper
                          component="a"
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            textDecoration: 'none',
                            backgroundColor: '#f9fafb',
                            transition: 'all 0.3s',
                            '&:hover': {
                              backgroundColor: '#eff6ff',
                              boxShadow: 2,
                              '& .download-icon': {
                                transform: 'scale(1.1)',
                              },
                            },
                          }}
                        >
                          <DownloadIcon
                            className="download-icon"
                            sx={{ fontSize: 24, color: 'info.main', transition: 'transform 0.3s' }}
                          />
                          <Box>
                            <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                              {resource.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {resource.subtitle}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default TakeAction;

